import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'

Vue.use(VueRouter)

import Layout from '@/views/Layout/index.vue'
import Login from '@/views/Login/index.vue'
import Dashboard from '@/views/Dashboard/index.vue'
import Products from '@/views/Products/index.vue'
import Orders from '@/views/Orders/index.vue'
import OrderDetail from '@/views/Orders/detail.vue'
import Members from '@/views/Members/index.vue'
import Finance from '@/views/Finance/index.vue'
import Withdraw from '@/views/Withdraw/index.vue'
import Banners from '@/views/Banners/index.vue'
import Settings from '@/views/Settings/index.vue'
import AfterSales from '@/views/AfterSales/index.vue'
import AfterSaleDetail from '@/views/AfterSales/detail.vue'
import DebtDetail from '@/views/DebtDetail/index.vue'
import CloudWarehouse from '@/views/CloudWarehouse/index.vue'
import SystemAdmins from '@/views/System/Admins.vue'
import SystemRoles from '@/views/System/Roles.vue'
import SystemPermissions from '@/views/System/Permissions.vue'
import NoPermission from '@/views/Error/NoPermission.vue'

// meta.permission 必须与后台「权限管理」(supplier_permissions.slug) 一致，
// 取值可以是字符串或数组（数组表示任一权限即可）。
// 没有配置 permission 的路由只要求登录（如详情页随父页面权限控制）。
const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { title: '登录' }
  },
  {
    path: '/',
    component: Layout,
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: { title: '首页数据', icon: 'el-icon-s-data', permission: 'dashboard' }
      },
      {
        path: 'products',
        name: 'Products',
        component: Products,
        meta: { title: '商品管理', icon: 'el-icon-goods', permission: 'products' }
      },
      {
        path: 'orders',
        name: 'Orders',
        component: Orders,
        meta: { title: '订单管理', icon: 'el-icon-tickets', permission: 'orders' }
      },
      {
        path: 'orders/:id',
        name: 'OrderDetail',
        component: OrderDetail,
        meta: { title: '订单详情', hidden: true, permission: 'orders' }
      },
      {
        path: 'members',
        name: 'Members',
        component: Members,
        meta: { title: '本域会员', icon: 'el-icon-user-solid', permission: 'members' }
      },
      {
        path: 'finance',
        name: 'Finance',
        component: Finance,
        meta: { title: '财务资产', icon: 'el-icon-money', permission: 'finance' }
      },
      {
        path: 'withdraw',
        name: 'Withdraw',
        component: Withdraw,
        meta: { title: '提现申请', icon: 'el-icon-bank-card', permission: 'withdraw' }
      },
      {
        path: 'banners',
        name: 'Banners',
        component: Banners,
        meta: { title: '轮播图管理', icon: 'el-icon-picture', permission: 'banners' }
      },
      {
        path: 'settings',
        name: 'Settings',
        component: Settings,
        meta: { title: '分身商设置', icon: 'el-icon-setting', permission: 'settings' }
      },
      {
        path: 'after-sales',
        name: 'AfterSales',
        component: AfterSales,
        meta: { title: '售后管理', icon: 'el-icon-service', permission: 'after-sales' }
      },
      {
        path: 'after-sales/:id',
        name: 'AfterSaleDetail',
        component: AfterSaleDetail,
        meta: { title: '售后详情', hidden: true, permission: 'after-sales' }
      },
      {
        path: 'cloud-warehouse',
        name: 'CloudWarehouse',
        component: CloudWarehouse,
        meta: { title: '托管商品管理', icon: 'el-icon-box', permission: 'cloud-warehouse' }
      },
      {
        path: 'debt-detail',
        name: 'DebtDetail',
        component: DebtDetail,
        meta: { title: '欠费流水明细', hidden: true, permission: 'finance' }
      },
      // RBAC 系统管理
      {
        path: 'system/admins',
        name: 'SystemAdmins',
        component: SystemAdmins,
        meta: { title: '管理员管理', icon: 'el-icon-user', permission: 'system.admins' }
      },
      {
        path: 'system/roles',
        name: 'SystemRoles',
        component: SystemRoles,
        meta: { title: '角色管理', icon: 'el-icon-s-custom', permission: 'system.roles' }
      },
      {
        path: 'system/permissions',
        name: 'SystemPermissions',
        component: SystemPermissions,
        meta: { title: '权限管理', icon: 'el-icon-lock', permission: 'system.permissions' }
      },
      {
        path: '403',
        name: 'NoPermission',
        component: NoPermission,
        meta: { title: '无访问权限', hidden: true }
      }
    ]
  }
]

const router = new VueRouter({
  routes
})

/**
 * 判断当前账号是否拥有目标路由要求的权限
 */
function hasRoutePermission(to) {
  // 未绑定角色的超级管理员（如分身店主管理员 shop_branch.admin_id）不受权限限制
  if (store.state.isSuper) {
    return true
  }
  const required = to.meta && to.meta.permission
  if (!required) {
    return true
  }
  const owned = store.state.permissions || []
  const need = Array.isArray(required) ? required : [required]
  return need.some((slug) => owned.includes(slug))
}

// 登录守卫 + 用户信息获取 + 菜单权限校验
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('supplier_token')

  if (!token) {
    if (to.path === '/login') {
      next()
    } else {
      next('/login')
    }
    return
  }

  // 已登录还访问登录页：直接进第一个有权限的菜单
  if (to.path === '/login') {
    next(store.getters.firstAccessiblePath || '/403')
    return
  }

  const afterUserInfo = () => {
    // 根路径：跳第一个有权限的菜单（角色没勾「首页数据」就不会进首页）
    if (to.path === '/') {
      next(store.getters.firstAccessiblePath || '/403')
      return
    }

    if (!hasRoutePermission(to)) {
      next('/403')
      return
    }

    next()
  }

  if (!store.state.userInfoLoaded) {
    // 有 token 且还没拉取用户信息，先拉取
    store.dispatch('fetchUserInfo').then(() => {
      afterUserInfo()
    }).catch(() => {
      // token 失效（如登录过期），清除 token 后进入登录页，避免无限跳转循环
      store.dispatch('logout').finally(() => {
        next('/login')
      })
    })
  } else {
    afterUserInfo()
  }
})

export default router
